"use client";

import React from "react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Loader2, Zap, CheckCircle2 } from "lucide-react";
export default function ScaleYourContent() {
  return <section data-section-id="224054" id="get-in-touch" className="bg-background text-foreground relative overflow-hidden py-24 sm:py-32">
      {}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] sm:w-[800px] sm:h-[800px] bg-primary/10 rounded-full blur-[100px] animate-pulse pointer-events-none" />
      <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/stardust.png')] opacity-20 pointer-events-none mix-blend-overlay" />

      <div className="container relative z-10 mx-auto px-4 max-w-3xl">
        <div className="text-center mb-12">
          <h2 className="font-default text-5xl sm:text-7xl font-extrabold tracking-tighter mb-4 uppercase text-transparent bg-clip-text bg-gradient-to-r from-foreground to-foreground/70">
            GET IN TOUCH
          </h2>
          <p className="text-muted-foreground font-sans text-lg sm:text-xl max-w-2xl mx-auto">
            Ready to elevate your project? Send us your details and let's build something extraordinary together.
          </p>
        </div>

        <div className="bg-card/40 backdrop-blur-xl border border-primary/20 rounded-lg p-6 sm:p-10 shadow-2xl">
          <FormProvider formKey="get_in_touch_form_home" formId="get_in_touch_form_home" sectionName="get-in-touch" defaultValues={{
          name: "",
          email: "",
          phone: "",
          video_length: "",
          platforms: [],
          deadline: "",
          details: ""
        }} validationRules={{
          name: {
            required: "Name is required"
          },
          email: {
            required: "Email is required",
            pattern: {
              value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
              message: "Invalid email address"
            }
          },
          video_length: {
            required: "Scope estimation is required",
            min: {
              value: 1,
              message: "Must be at least 1 unit"
            }
          },
          platforms: {
            required: "Select at least one service focus"
          }
        }} submitText="SECURE YOUR SLOT" successMessage="Your message has been received! Our specialists will review your brief and contact you within 24 hours.">
            <ScaleContentForm />
          </FormProvider>
        </div>
      </div>
    </section>;
}
function ScaleContentForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center py-12 text-center space-y-6 animate-in fade-in zoom-in duration-500">
        <div className="size-20 rounded-full bg-accent/20 flex items-center justify-center">
          <CheckCircle2 className="size-10 text-[#10B981]" />
        </div>
        <div className="space-y-2">
          <h3 className="font-default text-3xl font-bold text-foreground">Message Received</h3>
          <p className="text-muted-foreground font-sans max-w-md mx-auto">
            {successMessage}
          </p>
        </div>
        <Button onClick={resetForm} variant="outline" className="mt-4 border-primary/50 hover:bg-primary/10 text-foreground font-sans">
          Send Another Message
        </Button>
      </div>;
  }
  const inputClasses = "flex w-full rounded-md border border-border bg-background/80 px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground/40 transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:border-primary disabled:cursor-not-allowed disabled:opacity-50";
  return <Form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
        <FormField control={control} name="name" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground/90 font-sans font-medium">Full Name</FormLabel>
              <FormControl>
                <input type="text" className={`${inputClasses} h-12`} placeholder="John Doe" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="email" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground/90 font-sans font-medium">Email Address</FormLabel>
              <FormControl>
                <input type="email" className={`${inputClasses} h-12`} placeholder="john@example.com" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
        <FormField control={control} name="phone" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground/90 font-sans font-medium">Phone Number <span className="text-muted-foreground text-xs font-normal">(Optional)</span></FormLabel>
              <FormControl>
                <input type="tel" className={`${inputClasses} h-12`} placeholder="+1 555-123-4567" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="video_length" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground/90 font-sans font-medium">Project Scope (Est. Hours)</FormLabel>
              <FormControl>
                <input type="number" min="1" className={`${inputClasses} h-12`} placeholder="20" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="platforms" render={({
      field
    }) => <FormItem>
            <FormLabel className="text-foreground/90 font-sans font-medium">Service Focus</FormLabel>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 pt-2">
              {["Strategy", "Execution", "Management"].map((platform, i) => <label key={platform} data-index={i} className={`flex items-center space-x-3 rounded-md border p-3 cursor-pointer transition-all ${field.value?.includes(platform) ? "border-primary bg-primary/10 shadow-sm" : "border-border bg-background/50 hover:bg-background/80"}`}>
                  <input type="checkbox" className="sr-only" checked={field.value?.includes(platform) || false} onChange={e => {
            const current = field.value || [];
            const updated = e.target.checked ? [...current, platform] : current.filter((v: string) => v !== platform);
            field.onChange(updated);
          }} />
                  <div className={`size-4 rounded border flex items-center justify-center transition-colors ${field.value?.includes(platform) ? "border-primary bg-primary" : "border-muted-foreground"}`}>
                    {field.value?.includes(platform) && <CheckCircle2 className="size-3 text-primary-foreground" />}
                  </div>
                  <span className="text-sm font-sans font-medium">{platform}</span>
                </label>)}
            </div>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="deadline" render={({
      field
    }) => <FormItem>
            <FormLabel className="text-foreground/90 font-sans font-medium">Target Launch Date <span className="text-muted-foreground text-xs font-normal">(Optional)</span></FormLabel>
            <FormControl>
              <input type="date" className={`${inputClasses} h-12 [color-scheme:dark]`} {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="details" render={({
      field
    }) => <FormItem>
            <FormLabel className="text-foreground/90 font-sans font-medium">Project Brief / Goals <span className="text-muted-foreground text-xs font-normal">(Optional)</span></FormLabel>
            <FormControl>
              <textarea className={`${inputClasses} min-h-[120px] resize-y font-sans`} placeholder="Tell us about your vision and what you want to achieve..." {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-md text-destructive text-sm font-sans">
          {submitError}
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full h-16 text-xl sm:text-2xl font-default font-extrabold tracking-widest bg-primary hover:bg-primary/90 text-primary-foreground shadow-[0_0_20px_rgba(37,99,235,0.4)] hover:shadow-[0_0_30px_rgba(37,99,235,0.6)] bg-gradient-to-r from-primary via-primary/95 to-primary transition-all duration-300 rounded-lg group">
        {isSubmitting ? <>
            <Loader2 className="animate-spin mr-3 size-6" />
            PROCESSING...
          </> : <>
            <Zap className="mr-3 size-6 group-hover:scale-110 transition-transform" />
            SECURE YOUR SLOT
          </>}
      </Button>
    </Form>;
}